// src/services/api/customerNotesAPI.js
import { getAuth } from 'firebase/auth';

const FIREBASE_FUNCTIONS_BASE_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_BASE_URL;
const CUSTOMER_NOTES_ENDPOINT = `${FIREBASE_FUNCTIONS_BASE_URL}/customerNotes`;

/**
 * Customer Notes API - Handles all customer notes operations
 */
export class CustomerNotesAPI {
  /**
   * Get authentication token for API requests
   */
  static async getAuthToken() {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('User not authenticated');
    }

    return await user.getIdToken();
  }

  /**
   * Get all notes for a customer
   * @param {string} customerId - Customer ID
   * @returns {Promise<Array>} Array of notes
   */
  static async getCustomerNotes(customerId) {
    try {
      if (!customerId) {
        throw new Error('Customer ID is required');
      }

      const token = await this.getAuthToken();

      const response = await fetch(
        `${CUSTOMER_NOTES_ENDPOINT}?operation=getByCustomer&customerId=${customerId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch customer notes');
      }

      const data = await response.json();
      return data.data || [];

    } catch (error) {
      console.error('Error fetching customer notes:', error);
      throw error;
    }
  }

  /**
   * Create a new note for a customer
   * @param {string} customerId - Customer ID
   * @param {string} noteText - Note text content
   * @returns {Promise<Object>} Created note
   */
  static async createNote(customerId, noteText) {
    try {
      if (!customerId) {
        throw new Error('Customer ID is required');
      }

      if (!noteText || !noteText.trim()) {
        throw new Error('Note text is required');
      }

      const token = await this.getAuthToken();

      const response = await fetch(CUSTOMER_NOTES_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          operation: 'create',
          customerId,
          noteText: noteText.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create note');
      }

      const data = await response.json();
      return data.data;

    } catch (error) {
      console.error('Error creating note:', error);
      throw error;
    }
  }

  /**
   * Update an existing note
   * @param {string} noteId - Note ID
   * @param {string} noteText - Updated note text content
   * @returns {Promise<Object>} Updated note
   */
  static async updateNote(noteId, noteText) {
    try {
      if (!noteId) {
        throw new Error('Note ID is required');
      }

      if (!noteText || !noteText.trim()) {
        throw new Error('Note text is required');
      }

      const token = await this.getAuthToken();

      const response = await fetch(CUSTOMER_NOTES_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          operation: 'update',
          noteId,
          noteText: noteText.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update note');
      }

      const data = await response.json();
      return data.data;

    } catch (error) {
      console.error('Error updating note:', error);
      throw error;
    }
  }

  /**
   * Delete a note
   * @param {string} noteId - Note ID
   * @returns {Promise<Object>} Deletion result
   */
  static async deleteNote(noteId) {
    try {
      if (!noteId) {
        throw new Error('Note ID is required');
      }

      const token = await this.getAuthToken();

      const response = await fetch(CUSTOMER_NOTES_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          operation: 'delete',
          noteId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete note');
      }

      const data = await response.json();
      return data.data;

    } catch (error) {
      console.error('Error deleting note:', error);
      throw error;
    }
  }

  /**
   * Get a single note by ID
   * @param {string} noteId - Note ID
   * @returns {Promise<Object>} Note data
   */
  static async getNoteById(noteId) {
    try {
      if (!noteId) {
        throw new Error('Note ID is required');
      }

      const token = await this.getAuthToken();

      const response = await fetch(
        `${CUSTOMER_NOTES_ENDPOINT}?operation=getById&noteId=${noteId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch note');
      }

      const data = await response.json();
      return data.data;

    } catch (error) {
      console.error('Error fetching note:', error);
      throw error;
    }
  }
}
